import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { replace, toLower, omit, get, pick, size } from "lodash"
import { validatePortfolioName } from "helpers/portfolios"
import { Info } from "@mui/icons-material"
import CurrencySelector from "components/currency-selector"
import { TextField, FormHelperText, FormControl, Checkbox, FormControlLabel, DialogContent, DialogActions, Button, Tooltip } from "@mui/material"
import { CURRENCY } from "helpers/shapes"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"
import BetaInstrumentSelector from "./components/betaInstrumentSelector"
import "./style.scss"

function EditPortfolio({
  isOpPro,
  account,
  accountDefaultCurrencyId,
  supportedCurrencies,
  portfolio,
  close,
  applyAction,
  savePortfolio,
  models,
  defaultModelId,
  allowShowPortfolio,
  linkedToAlphaTheory,
  isIntradayAccountEnabled,
  betaInstrumentMeta,
}) {
  const initialModelId = get(portfolio, "modelId", defaultModelId ?? "")
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [selectedBetaInstrument, setSelectedBetaInstrument] = useState(betaInstrumentMeta)
  const [betaInstrumentUpdated, setBetaInstrumentUpdated] = useState(false)
  const [name, setName] = useState(get(portfolio, "name", ""))
  const [alias, setAlias] = useState(get(portfolio, "alias", ""))
  const [description, setDescription] = useState(get(portfolio, "description", ""))
  const [modelId, setModelId] = useState(initialModelId)
  const [live, setLive] = useState(!get(portfolio, "id") ? true : get(portfolio, "live", false))
  const [intraday, setIntraday] = useState(get(portfolio, "intradayEnabled", false))
  const [currencyOverride, setCurrencyOverride] = useState(get(portfolio, "currencyOverride"))
  const [fxHedgeEnabled, setFxHedgeEnabled] = useState(get(portfolio, "fxHedgeEnabled", false))

  const [currencyOverrideChecked, setCurrencyOverrideChecked] = useState(!!get(portfolio, "currencyOverride"))
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setIsSubmitDisabled(!name || !modelId || !alias || size(errors) > 0)
  }, [alias, errors, modelId, name])

  const updateBetaInstrument = (betaInstrument) => {
    setSelectedBetaInstrument(betaInstrument)
    setBetaInstrumentUpdated(true)
  }

  const getBetaResourceForSave = () => {
    const { type, id, assetClass, ticker } = selectedBetaInstrument ?? {}
    if (type === "portfolio") {
      return {
        type: "PORTFOLIO",
        id,
      }
    } else if (assetClass === "swap") {
      return {
        type: "SWAP",
        id: ticker,
      }
    }
    return null
  }

  const getBetaSecurityForSave = () => {
    if (get(selectedBetaInstrument, "assetSubclass") === "etf") {
      return pick(selectedBetaInstrument, ["mic", "ticker", "figi", "bloombergTicker", "internalId"])
    }
    return null
  }

  const getBetaInstrumentForSave = () => {
    if (!betaInstrumentUpdated) {
      return
    }

    const { type, id } = selectedBetaInstrument ?? {}
    return {
      securityId: getBetaSecurityForSave(),
      resource: getBetaResourceForSave(),
      benchmarkId: type === "benchmark" ? id : null,
      indexId: type === "index" ? id : null,
    }
  }

  const submitPortfolio = (showPortfolio) => {
    savePortfolio({ name, description, modelId, live, alias, intraday, currencyOverride, betaInstrument: getBetaInstrumentForSave(), fxHedgeEnabled }, showPortfolio)
  }

  const handleSubmit = (showPortfolio) => {
    const currencyOverrideMissing = currencyOverrideChecked && !currencyOverride

    if (currencyOverrideMissing) {
      setErrors({ ...errors, currencyOverride: true })
    }

    if (size(errors) > 0 || currencyOverrideMissing) {
      setIsSubmitDisabled(true)
    } else {
      applyAction(() => submitPortfolio(showPortfolio), null, null, true)()
    }
  }

  const handleNameChanged = (event) => {
    const newName = event.target.value

    setName(newName)
    setAlias(replace(toLower(newName), /[\s-]/g, "_"))

    if (!validatePortfolioName(newName) && !errors.name) {
      setErrors({ ...errors, name: true })
    } else if (validatePortfolioName(newName) && errors.name) {
      setErrors(omit(errors, "name"))
    }
  }

  const handleCurrencyOverrideChecked = (event) => {
    setCurrencyOverrideChecked(event.target.checked)
    setCurrencyOverride(null)
    setErrors(omit(errors, "currencyOverride"))
  }

  const handleCurrencyOverrideChanged = (_, currencyId) => {
    if (currencyId) {
      setErrors(omit(errors, "currencyOverride"))
    } else {
      setErrors({ ...errors, currencyOverride: true })
    }
    setCurrencyOverride(currencyId)
  }

  const onKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      handleSubmit(allowShowPortfolio)
    }
  }

  const createPrefix = get(portfolio, "id") ? "Update" : "Create"
  let createPortfolioLabel
  if (allowShowPortfolio) {
    createPortfolioLabel = `${createPrefix} and show`
  } else {
    createPortfolioLabel = `${createPrefix} portfolio`
  }

  let nameHelperText
  if (linkedToAlphaTheory) {
    nameHelperText = "This portfolio is synced to Alpha Theory and cannot be changed"
  } else if (errors.name) {
    nameHelperText = "Invalid name. Please follow naming guidelines."
  } else {
    nameHelperText = "3-50 chars long. Alphanum, _, -, and spaces."
  }

  return <div className="edit-portfolio-wrapper">
    <DialogContent className="edit-portfolio-content-wrapper">
      <div className="name-input-wrapper">
        <FormControl variant="standard">
          <TextField
            variant="standard"
            id="portfolio-name"
            name="name"
            className="name-input"
            label="Portfolio Name"
            value={name}
            error={errors.name}
            onKeyDown={onKeyPress}
            onChange={handleNameChanged}
            margin="normal"
            helperText={nameHelperText}
            disabled={linkedToAlphaTheory}
            slotProps={{
              formHelperText: { error: errors.name, classes: { root: "portfolio-name-error" } },
            }}
          />
        </FormControl>
        {!get(portfolio, "id")
          && <FormControl variant="standard">
            <TextField
              variant="standard"
              disabled
              name="alias"
              error={errors.alias}
              className="name-input"
              label="Alias"
              value={alias}
              margin="normal"
            />
          </FormControl>}
      </div>
      <FormControl variant="standard">
        <TextField
          variant="standard"
          id="portfolio-description"
          name="description"
          className="name-description"
          label="Portfolio Description"
          value={description}
          multiline
          error={errors.description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={onKeyPress}
          margin="normal"
        />
      </FormControl>
      <FormControl variant="standard" className="model" error={errors.modelId}>
        <ModelSelectorAutocomplete
          className="model-select-input"
          models={models}
          value={modelId}
          onChange={(id) => {
            updateBetaInstrument()
            setModelId(id)
          }}
          label="Risk Model"
          inputId="modelId"
          textFieldProps={{ name: "modelId", error: !!errors.modelId }}
        />
        {errors.modelId && <FormHelperText>Please select a model</FormHelperText>}
      </FormControl>
      {!isOpPro && <FormControl variant="standard">
        <BetaInstrumentSelector account={account} modelId={modelId} betaInstrument={selectedBetaInstrument} handleInstrumentSelected={updateBetaInstrument} />
      </FormControl>}
      <FormControl variant="standard" className="optional-setting">
        <FormControlLabel
          id="rollover-postions-setting"
          className="optional-setting-label"
          control={
            <Checkbox
              name="live"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
              value="live"
            />
          }
          label="Rollover positions to current date"
        />
        <Tooltip
          classes={{ tooltipPlacementBottom: "field-info-popper" }}
          className="field-info"
          title="This will take the last uploaded position data and carry that information forward to the model's current date."
        >
          <Info />
        </Tooltip>
      </FormControl>
      {isIntradayAccountEnabled && <FormControl variant="standard" className="optional-setting">
        <FormControlLabel
          id="intraday-setting"
          className="optional-setting-label"
          control={
            <Checkbox
              name="intraday"
              checked={intraday}
              onChange={(e) => setIntraday(e.target.checked)}
              value="intraday"
            />
          }
          label="Intraday"
        />
      </FormControl>}
      <FormControl variant="standard">
        <FormControlLabel
          id="currency-override-setting"
          className="optional-setting"
          control={
            <Checkbox
              checked={currencyOverrideChecked}
              onChange={handleCurrencyOverrideChecked}
            />
          }
          label={`Override default account currency (${accountDefaultCurrencyId})`}
        />
      </FormControl>
      {currencyOverrideChecked && <FormControl variant="standard">
        <CurrencySelector
          supportedCurrencies={supportedCurrencies}
          value={currencyOverride}
          onChange={handleCurrencyOverrideChanged}
          onKeyPress={onKeyPress}
          error={errors.currencyOverride}
        />
        {errors.currencyOverride && <FormHelperText className="currency-override-error">Please select a currency</FormHelperText>}
      </FormControl>}
      <FormControl variant="standard" className="optional-setting">
        <FormControlLabel
          id="fx-hedge-enabled-setting"
          className="optional-setting-label"
          control={
            <Checkbox
              name="fxHedgeEnabled"
              checked={fxHedgeEnabled}
              onChange={(e) => setFxHedgeEnabled(e.target.checked)}
              value="fxHedgeEnabled"
            />
          }
          label={<>Enable FX hedging<sup> beta</sup></>}
        />
        <Tooltip
          classes={{ tooltipPlacementBottom: "field-info-popper" }}
          className="field-info"
          title="This enables FX hedging to remove currency risk based on the default currency of the portfolio."
        >
          <Info />
        </Tooltip>
      </FormControl>
    </DialogContent>
    <DialogActions>
      <Button color="secondary" onClick={close}>Cancel</Button>
      {allowShowPortfolio
        && <Button color="secondary" disabled={isSubmitDisabled} onClick={() => handleSubmit()}>
          {`${createPrefix} and close`}
        </Button>}
      <Button color="primary" disabled={isSubmitDisabled} onClick={() => handleSubmit(allowShowPortfolio)}>
        {createPortfolioLabel}
      </Button>
    </DialogActions>
  </div>
}

EditPortfolio.propTypes = {
  account: PropTypes.string.isRequired,
  defaultModelId: PropTypes.string,
  portfolioId: PropTypes.string,
  portfolio: PropTypes.object,
  close: PropTypes.func,
  applyAction: PropTypes.func,
  positionSet: PropTypes.bool,
  savePortfolio: PropTypes.func.isRequired,
  models: PropTypes.array.isRequired,
  allowShowPortfolio: PropTypes.bool,
  linkedToAlphaTheory: PropTypes.bool,
  isIntradayAccountEnabled: PropTypes.bool,
  betaInstrument: PropTypes.object,
  betaInstrumentMeta: PropTypes.object,
  isOpPro: PropTypes.bool,
  accountDefaultCurrencyId: PropTypes.string,
  currencyOverride: CURRENCY,
  supportedCurrencies: PropTypes.arrayOf(CURRENCY),
}

export default EditPortfolio
